import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as hr from '../api';
import { errorText } from '../../utils';
import { fmtMoney, has, titleCase } from '../format';
import { hrSubUrl } from '../catalog';
import { ChartCard, KpiGrid, groupCount } from '../components/Charts';
import StatusPill from '../components/StatusPill';
import Loader from '../components/Loader';

// Workforce KPIs, distributions and alerts — the reference "employees" dashboard, computed from the register.
// Payroll and loan figures appear once those modules exist.

const PAID = ['active', 'probation'];

export default function EmployeeCommandCenter() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { hr.listEmployees().then(setRows).catch(e => setError(errorText(e))); }, []);

  if (error) return <div className="error">{error}</div>;
  if (!rows) return <Loader title="Loading dashboard…" subtitle="Preparing workforce KPIs" />;

  const st = e => e.employment_status;
  const count = fn => rows.filter(fn).length;
  const paid = rows.filter(e => PAID.includes(st(e)));
  const missing = k => paid.filter(e => !has(e[k])).length;
  const go = params => navigate(hrSubUrl('manage-employees', params));

  const kpis = [
    { label: 'Total Employees', value: rows.length, icon: 'users', onClick: () => go() },
    { label: 'Active', value: count(e => st(e) === 'active'), tone: 'good', icon: 'user-check', onClick: () => go({ status: 'active' }) },
    { label: 'Probation', value: count(e => st(e) === 'probation'), icon: 'hourglass', onClick: () => go({ status: 'probation' }) },
    { label: 'Suspended', value: count(e => st(e) === 'suspended'), tone: 'warn', icon: 'user-x', onClick: () => go({ status: 'suspended' }) },
    { label: 'Inactive / Exited', value: count(e => ['inactive', 'exited'].includes(st(e))), icon: 'log-out' },
    { label: 'Private Employees', value: count(e => e.is_private), icon: 'lock' },
    { label: 'Draft Records', value: count(e => ['draft', 'onboarding'].includes(st(e))), tone: 'info', icon: 'file-pen-line', onClick: () => go({ status: 'draft' }) },
    { label: 'Monthly Salary Bill', value: paid.reduce((t, e) => t + (Number(e.monthly_gross ?? e.basic_salary) || 0), 0), format: 'money' },
    { label: 'Missing Bank Details', value: missing('bank_account_number'), tone: 'bad', icon: 'landmark', onClick: () => navigate(hrSubUrl('employee-master', { missing: 1 })) },
    { label: 'Missing Pension ID', value: missing('pension_rsa_pin'), tone: 'bad', icon: 'shield-alert', onClick: () => navigate(hrSubUrl('employee-master', { missing: 1 })) },
    { label: 'Missing Tax ID', value: missing('tax_identification_number'), tone: 'bad', icon: 'receipt', onClick: () => navigate(hrSubUrl('employee-master', { missing: 1 })) },
  ];

  const charts = [
    { title: 'Employees by Department', type: 'bar', data: groupCount(rows, e => e.department_name) },
    { title: 'Status Distribution', type: 'pie', data: groupCount(rows, st) },
    { title: 'Employment Type', type: 'pie', data: groupCount(rows, e => e.employment_type) },
    { title: 'By Project Site', type: 'bar', data: groupCount(rows, e => e.site_name) },
  ];
  const recent = [...rows].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))).slice(0, 6);

  return (
    <>
      <KpiGrid kpis={kpis} />
      <div className="chart-grid">{charts.map(c => <ChartCard key={c.title} chart={c} money={false} />)}</div>
      <div className="panel">
        <h3 className="panel-title">Recent Employees</h3>
        {recent.length === 0 ? <div className="empty">Nothing here</div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Employee ID</th><th>Full Name</th><th>Department</th><th>Status</th><th className="num">Monthly Salary</th></tr></thead>
              <tbody>
                {recent.map(e => (
                  <tr key={e.id} className="clickable" onClick={() => navigate(hrSubUrl('employee-360', { id: e.id }))}>
                    <td>{e.employee_no}</td><td>{e.full_name}</td><td>{e.department_name || '—'}</td>
                    <td><StatusPill value={st(e)} /></td><td className="num">{fmtMoney(e.monthly_gross ?? e.basic_salary)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {rows.length === 0 && <div className="alert info">No employees yet. Use <b>Add Employee</b> to create the first record — {titleCase('onboarding')} starts there.</div>}
    </>
  );
}
